type ObserverTrigger = 'scrolled-past' | 'not-reached' | 'is-hidden' | 'is-visible';

type ElementViewportObserverCallback = (isIntersecting: boolean) => void;

type ElementViewportObserverOptions = {
    threshold?: number | number[];
    rootMargin?: string;
    trigger?: ObserverTrigger;
    root?: Element | Document | null;
};

class ElementViewportObserver {
    private observer: IntersectionObserver;

    private readonly trigger: ObserverTrigger;

    constructor(callback: ElementViewportObserverCallback, options: ElementViewportObserverOptions = {}) {
        this.trigger = options.trigger || 'is-hidden';

        const handleObserver = ([entry]: IntersectionObserverEntry[]) => callback(this.handleIsIntersecting(entry));

        const observerOptions = {
            threshold: options.threshold ?? 0,
            rootMargin: options.rootMargin ?? '0px',
            root: options.root ?? undefined
        };

        this.observer = new IntersectionObserver(handleObserver, observerOptions);
    }

    private handleIsIntersecting(entry: IntersectionObserverEntry): boolean {
        const { top, bottom } = entry.boundingClientRect;
        const { isIntersecting, rootBounds } = entry;

        const rootTop = rootBounds?.top ?? 0;
        const rootBottom = rootBounds?.bottom ?? window.innerHeight;

        const isIntersectingConditions: Record<ObserverTrigger, boolean> = {
            'scrolled-past': !isIntersecting && top < rootTop,
            'not-reached': !isIntersecting && bottom > rootBottom,
            'is-hidden': !isIntersecting,
            'is-visible': isIntersecting
        };

        const isIntersectionAccordingToConditions = isIntersectingConditions[this.trigger];

        return isIntersectionAccordingToConditions ?? false;
    }

    observe(element: HTMLElement) {
        this.observer.observe(element);
    }

    unobserve(element: HTMLElement) {
        this.observer.unobserve(element);
    }

    disconnect() {
        this.observer.disconnect();
    }
}

export default ElementViewportObserver;
